// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import apiEnvironments from '@maxpreps/network/src/apiEnvironments';
import {
  adminOrigin,
  publicOrigin,
  secureOrigin
} from '@maxpreps/network/src/origins';
import { init, setTag } from '@sentry/nextjs';
import { BrowserTracing } from '@sentry/tracing';
import getNextConfig from './../../constants/getNextConfig';
import getSharedConfig, { InitSentryOptions } from './sentry.shared.config';

const { appEnv, appName } = getNextConfig().publicRuntimeConfig;

// solution barrowed from here with minor mods (edg is the canary version; edge is still in prod and what we care to filter out)
// https://bytenota.com/javascript-detect-and-get-the-current-version-of-microsoft-edge/#:~:text=To%20detect%20whether%20or%20not,extract%20the%20version%20from%20it.
function isMicrosoftEdgeBrowser(userAgent): boolean {
  return userAgent.indexOf('edge/') > -1 || userAgent.indexOf('edg/') > -1;
}

function getMicrosoftEdgeBrowserVersion() {
  if (typeof window === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent.toLowerCase();

  if (!isMicrosoftEdgeBrowser(userAgent)) {
    return null;
  }

  const matches = userAgent.match(
    /(edg|edge)\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+)/
  );
  let version;

  if (matches) {
    [, , version] = matches;
  }

  return version;
}

function getMicrosoftEdgeMajorVersion() {
  const edgeBrowserVersion = getMicrosoftEdgeBrowserVersion();
  if (!edgeBrowserVersion) {
    return undefined;
  }

  return Number.parseInt(edgeBrowserVersion.split('.')[0]);
}

const initSentry = (
  options: InitSentryOptions = {
    application: appName || 'not set',
    // .2 (and not 1) mostly because of ads, but also because we are making a big sentry update.
    // I don't know what new stuff is going to come through
    sampleRate: appEnv === 'production' ? 0.2 : 1
  }
) => {
  const microsoftEdgeMajorVersion = getMicrosoftEdgeMajorVersion();

  init({
    ...getSharedConfig(options), // https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
    allowUrls: [
      // first party stuff
      /^https?:\/\/maxpreps(-staging|-development){0,1}\.cbsistatic\.com.*\.js/i,
      /^https?:\/\/asset(-staging|-development){0,1}\.maxpreps\.io.*\.js/i,
      /^https?:\/\/(www|staging|dev|branch-[a-z]\.fe|z)\.maxpreps\.com.*\.js/i

      // third party stuff libs
      // /googleoptimize\.com/i,
      // /google-analytics\.com/i,
      // /tags\.tiqcdn\.com/i

      // Other cbsi controlled values
      // https://production-cmp.isgprivacy.cbsi.com
      // https://at.cbsi.com/
    ],
    // denyUrls: [
    //   // Facebook blocked
    //   // /graph\.facebook\.com/i,
    //   // /connect\.facebook\.net\/en_US\/all\.js/i,
    //   // Chrome extensions
    //   // /extensions\//i,
    //   // /^chrome:\/\//i,
    //   // /yieldmo\.com\//i,
    //   // /at\.cbsi\.com\/lib\/dist\/prod\/bidbarrel-maxpreps\.min\.js/i
    // ],
    integrations: [
      new BrowserTracing({
        tracingOrigins: [
          apiEnvironments[appEnv],
          adminOrigin,
          publicOrigin,
          secureOrigin,
          /^\//
        ]
      })
    ],
    beforeSend(event) {
      // at the time of writing (rb: 1/20/2021) 80+ represented 90% of edge.
      // edge represented 2% of total site traffic
      if (microsoftEdgeMajorVersion && microsoftEdgeMajorVersion < 80) {
        return null;
      }

      return event;
    }
  });

  if (options.application) setTag('application', options.application);
};

export default initSentry;
